(ns hypnos.translator.communication
  "Translators between Clojure and GraphQL for the communication models."
  (:require [clojure.spec.alpha :as s]
            [clojure.string :as str]
            [hypnos.time :as t]
            ;; [hypnos.translator.patient :as t-p]
            [medley.core :as mc]))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Specs

;; communication-status
(def ^{:const true :private true} communication-statuses
  #{:communication-status/new
    :communication-status/in-progress
    :communication-status/sent
    :communication-status/failed})
(s/def ::communication-status communication-statuses)
(s/def ::communication-status-gql (set (map name communication-statuses)))

;; communication-type
(s/def ::communication-type keyword?)
(s/def ::communication-type-gql string?)

;; communication-channel
(def ^{:const true :private true} communication-channels
  #{:communication-channel/email
    :communication-channel/sms})
(s/def ::communication-channel communication-channels)
(s/def ::communication-channel-gql (set (map name communication-channels)))

;; provider
(def ^{:const true :private true} providers
  #{:provider/sendgrid
    :provider/twilio})
(s/def ::provider providers)
(s/def ::provider-gql (set (map name providers)))

;; communication

(s/def :communication/id uuid?)
(s/def :communication/communication-type ::communication-type)
(s/def :communication/communication-channel ::communication-channel)
(s/def :communication/to string?)
(s/def :communication/subject string?)
(s/def :communication/communicated-at inst?)
(s/def :communication/provider ::provider)
(s/def :communication/provider-key string?)
(s/def :communication/status ::communication-status)
;; (s/def :communication/recipient :hypnos/recipient)

(s/def :communication.gql/id string?)
(s/def :communication.gql/communication_type ::communication-type-gql)
(s/def :communication.gql/communication_channel ::communication-channel-gql)
(s/def :communication.gql/to string?)
(s/def :communication.gql/subject string?)
(s/def :communication.gql/communicated_at string?)
(s/def :communication.gql/provider ::provider-gql)
(s/def :communication.gql/provider_key string?)
(s/def :communication.gql/status ::communication-status-gql)
;; (s/def :communication.gql/recipient :hypnos.gql/recipient)

(s/def :hypnos/communication
  (s/keys :req [:communication/id
                :communication/communication-type
                :communication/communication-channel
                :communication/to
                :communication/subject
                :communication/communicated-at
                :communication/provider
                :communication/provider-key
                :communication/status
                ;; :communication/patient
                ]))

(s/def :hypnos.gql/communication
  (s/keys :req-un [:communication.gql/id
                   :communication.gql/communication-type
                   :communication.gql/communication-channel
                   :communication.gql/to
                   :communication.gql/subject
                   :communication.gql/communicated-at
                   :communication.gql/provider
                   :communication.gql/provider-key
                   :communication.gql/status
                   ;; :communication.gql/patient
                   ]))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Helpers

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Enums

(defn communication-status->clj
  [s]
  (when-not (str/blank? s)
    (keyword (str "communication-status/" (str/replace s #"_" "-")))))

(s/fdef communication-status->clj
  :args (s/cat :s (s/or :status ::communication-status-gql :nil nil?))
  :ret (s/or :status ::communication-status :nil nil?))

(defn communication-status->gql
  [o]
  (when (keyword? o)
    (str/replace (name o) #"-" "_")))

(s/fdef communication-status->gql
  :args (s/cat :o (s/or :status ::communication-status :nil nil?))
  :ret (s/or :status ::communication-status-gql :nil nil?))

(defn communication-type->clj
  [s]
  (when-not (str/blank? s)
    (keyword (str/replace s #"_" "-"))))

(s/fdef communication-type->clj
  :args (s/cat :s string?)
  :ret (s/or :type keyword? :nil nil?))

(defn communication-type->gql
  [o]
  (when (keyword? o)
    (str/replace (name o) #"-" "_")))

(s/fdef communication-type->gql
  :args (s/cat :o (s/or :type keyword? :nil nil?))
  :ret (s/or :type string? :nil nil?))

(defn communication-channel->clj
  [s]
  (when-not (str/blank? s)
    (keyword (str "communication-channel/" s))))

(s/fdef communication-channel->clj
  :args (s/cat :s (s/or :channel ::communication-channel-gql :nil nil?))
  :ret (s/or :channel ::communication-channel :nil nil?))

(defn communication-channel->gql
  [o]
  (when (keyword? o)
    (name o)))

(s/fdef communication-channel->gql
  :args (s/cat :o (s/or :channel ::communication-channel :nil nil?))
  :ret (s/or :channel ::communication-channel-gql :nil nil?))

(defn provider->clj
  [s]
  (when-not (str/blank? s)
    (keyword (str "provider/" s))))

(s/fdef provider->clj
  :args (s/cat :s (s/or :provider ::provider-gql :nil nil?))
  :ret (s/or :provider ::provider :nil nil?))

(defn provider->gql
  [o]
  (when (keyword? o)
    (name o)))

(s/fdef provider->gql
  :args (s/cat :o (s/or :provider ::provider :nil nil?))
  :ret (s/or :provider ::provider-gql :nil nil?))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Entities

(defn communication->clj
  "Convert the communication to the Clojure format."
  [m]
  (cond-> {}
    (:id m)                    (assoc :communication/id (mc/uuid (:id m)))
    (:communication_type m)    (assoc :communication/type (communication-type->clj (:communication_type m)))
    (:communication_channel m) (assoc :communication/channel (communication-channel->clj (:communication_channel m)))
    (:to m)                    (assoc :communication/to (:to m))
    (:subject m)               (assoc :communication/subject (:subject m))
    (:scheduled_at m)          (assoc :communication/scheduled-at (t/from-aws-date-time (:scheduled_at m)))
    (:communicated_at m)       (assoc :communication/communicated-at (t/from-aws-date-time (:communicated_at m)))
    (:provider m)              (assoc :communication/provider (provider->clj (:provider m)))
    (:provider_key m)          (assoc :communication/provider-key (:provider_key m))
    (:communication_status m)  (assoc :communication/status (communication-status->clj (:communication_status m)))
    ;; (:recipient m)             (assoc :communication/recipient (t-p/patient->clj (:recipient m)))
    ))

(s/fdef communication->clj
  :args (s/cat :m :hypnos.gql/communication)
  :ret  (s/and map? :hypnos/communication))

(defn communication->gql
  "Convert the communication to the GraphQL format."
  [m]
  (cond-> {}
    (:communication/id m)              (assoc :id (str (:communication/id m)))
    (:communication/type m)            (assoc :communication_type (communication-type->gql (:communication/type m)))
    (:communication/channel m)         (assoc :communication_channel (communication-channel->gql (:communication/channel m)))
    (:communication/to m)              (assoc :to (:communication/to m))
    (:communication/subject m)         (assoc :subject (:communication/subject m))
    (:communication/scheduled-at m)    (assoc :communicated_at (t/to-aws-date-time (:communication/scheduled-at m)))
    (:communication/communicated-at m) (assoc :communicated_at (t/to-aws-date-time (:communication/communicated-at m)))
    (:communication/provider m)        (assoc :provider (provider->gql (:communication/provider m)))
    (:communication/provider-key m)    (assoc :provider_key (:communication/provider-key m))
    (:communication/status m)          (assoc :communication_status (communication-status->gql (:communication/status m)))
    ;; (:communication/recipient m)       (assoc :recipient (t-p/patient->gql (:communication/recipient m)))
    ))

(s/fdef communication
  :args (s/cat :m :hypnos/communication)
  :ret  (s/and map? :hypnos.gql/communication))
